import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import api from "../../api/api";
import Swal from "sweetalert2";

import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  Assessment,
  CalendarMonth,
  CheckCircle,
  Close,
  Delete,
  Download,
  Edit,
  MenuBook,
  Save,
  School,
  Search,
  Timer,
} from "@mui/icons-material";

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import logo from "./savationChristLogo.png";

/* =========================================================
   THEME
========================================================= */

const COLORS = {
  black: "#111111",
  darkBlack: "#1A1A1A",

  orange: "#F28C28",
  darkOrange: "#D96F0B",

  yellow: "#D4A017",

  camel: "#C19A6B",
  lightCamel: "#F4E8D8",

  cream: "#FBF8F3",
  white: "#FFFFFF",

  gray: "#6B7280",
  darkGray: "#374151",

  lightGray: "#F5F5F5",
  border: "#D9D9D9",

  green: "#2E7D32",
  red: "#C62828",

  blue: "#1565C0",
};

/* =========================================================
   TEACHING MATERIALS
========================================================= */

const TEACHING_MATERIALS = [
  {
    value: "notebook",
    label: "Notebook",
  },
  {
    value: "chalk",
    label: "Chalk",
  },
  {
    value: "marker",
    label: "Marker",
  },
  {
    value: "lesson_book",
    label: "Lesson Book",
  },
  {
    value: "pen",
    label: "Pen",
  },
  {
    value: "duster",
    label: "Duster",
  },
  {
    value: "handout",
    label: "Handout",
  },
  {
    value: "pamphlet_booklet",
    label: "Pamphlet / Booklet",
  },
  {
    value: "poster",
    label: "Poster",
  },
  {
    value: "textbook",
    label: "Textbook",
  },
  {
    value: "workbook",
    label: "Workbook",
  },
  {
    value: "flash_cards",
    label: "Flash Cards",
  },
  {
    value: "chart",
    label: "Chart",
  },
  {
    value: "whiteboard",
    label: "Whiteboard",
  },
  {
    value: "projector",
    label: "Projector",
  },
  {
    value: "computer",
    label: "Computer",
  },
  {
    value: "other",
    label: "Other",
  },
];

const MATERIAL_LABELS = Object.fromEntries(
  TEACHING_MATERIALS.map((item) => [
    item.value,
    item.label,
  ])
);

/* =========================================================
   HELPERS
========================================================= */

const safeText = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "-";
  }

  return String(value);
};

const formatDate = (dateValue) => {
  if (!dateValue) {
    return "-";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const normalizeMaterials = (plan) => {
  if (
    Array.isArray(plan?.teaching_materials)
  ) {
    return plan.teaching_materials;
  }

  if (
    Array.isArray(
      plan?.teaching_materials_display
    )
  ) {
    return plan.teaching_materials_display;
  }

  return [];
};

const materialLabel = (value) => {
  return (
    MATERIAL_LABELS[value] ||
    value ||
    "Other"
  );
};

/* =========================================================
   MUI INPUT STYLE
========================================================= */

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: COLORS.white,
    transition: "all 0.2s ease",

    "& fieldset": {
      borderColor: COLORS.border,
    },

    "&:hover fieldset": {
      borderColor: COLORS.camel,
    },

    "&.Mui-focused fieldset": {
      borderColor: COLORS.orange,
      borderWidth: "2px",
    },
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: COLORS.darkOrange,
  },
};

/* =========================================================
   PDF STYLES
========================================================= */

const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 48,
    paddingLeft: 30,
    paddingRight: 30,
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
    color: "#243447",
  },

  schoolHeader: {
    borderWidth: 1,
    borderColor: "#D9E1E8",
    backgroundColor: "#F8FAFC",
    padding: 12,
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
    borderRadius: 4,
  },

  logo: {
    width: 58,
    height: 58,
    marginRight: 12,
  },

  schoolHeaderContent: {
    flex: 1,
    alignItems: "center",
  },

  schoolName: {
    fontSize: 17,
    fontWeight: "bold",
    color: "#1F3448",
    textAlign: "center",
    marginBottom: 3,
  },

  schoolAddress: {
    fontSize: 7.5,
    color: "#617181",
    textAlign: "center",
    marginBottom: 2,
  },

  schoolContact: {
    fontSize: 7.5,
    color: "#617181",
    textAlign: "center",
  },

  documentTitle: {
    marginTop: 9,
    backgroundColor: "#EAF1F6",
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: "#D4E0E8",
    borderBottomWidth: 3,
    borderBottomColor: "#C9A45C",
  },

  documentTitleText: {
    color: "#29465B",
    fontSize: 13,
    fontWeight: "bold",
    textAlign: "center",
    letterSpacing: 1,
  },

  topInformation: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#D9E1E8",
    backgroundColor: "#FFFFFF",
  },

  topInformationColumn: {
    width: "50%",
    padding: 8,
  },

  topInformationColumnRight: {
    width: "50%",
    padding: 8,
    borderLeftWidth: 1,
    borderLeftColor: "#E3E8ED",
  },

  informationRow: {
    flexDirection: "row",
    marginBottom: 4,
  },

  informationLabel: {
    width: 70,
    fontSize: 8,
    fontWeight: "bold",
    color: "#718096",
  },

  informationValue: {
    flex: 1,
    fontSize: 8.5,
    color: "#263746",
    lineHeight: 1.3,
  },

  section: {
    marginTop: 9,
    borderWidth: 1,
    borderColor: "#D9E1E8",
    borderRadius: 4,
  },

  sectionTitle: {
    backgroundColor: "#F2F6F9",
    color: "#29465B",
    fontSize: 8.5,
    fontWeight: "bold",
    paddingVertical: 5,
    paddingHorizontal: 7,
    textTransform: "uppercase",
    borderBottomWidth: 1,
    borderBottomColor: "#DCE4EA",
  },

  sectionBody: {
    padding: 8,
    backgroundColor: "#FFFFFF",
  },

  topicBody: {
    padding: 9,
    backgroundColor: "#FCFAF5",
    borderLeftWidth: 3,
    borderLeftColor: "#C9A45C",
  },

  topicText: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#263746",
    lineHeight: 1.35,
  },

  objectiveText: {
    fontSize: 8.5,
    lineHeight: 1.45,
    color: "#465665",
  },

  materialsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  material: {
    paddingVertical: 4,
    paddingHorizontal: 7,
    marginRight: 5,
    marginBottom: 5,
    borderWidth: 1,
    borderColor: "#D7C89E",
    borderRadius: 3,
    backgroundColor: "#FBF7EA",
  },

  materialText: {
    fontSize: 7.5,
    color: "#5D5748",
  },

  teachingAidBox: {
    padding: 8,
    backgroundColor: "#FFF9F0",
    borderLeftWidth: 3,
    borderLeftColor: "#F28C28",
  },

  teachingAidText: {
    fontSize: 8.5,
    lineHeight: 1.45,
    color: "#465665",
  },

  methodText: {
    fontSize: 8.5,
    lineHeight: 1.45,
    color: "#465665",
  },

  procedureSection: {
    marginTop: 9,
    borderWidth: 1,
    borderColor: "#D9E1E8",
    borderRadius: 4,
  },

  procedureHeader: {
    backgroundColor: "#EEF3F7",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#D8E1E8",
  },

  procedureHeaderText: {
    color: "#29465B",
    fontSize: 8.5,
    fontWeight: "bold",
    textAlign: "center",
    textTransform: "uppercase",
  },

  procedureRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E3E8ED",
  },

  procedureLabel: {
    width: "31%",
    padding: 7,
    backgroundColor: "#F8FAFC",
    borderRightWidth: 1,
    borderRightColor: "#E1E7EC",
    fontSize: 8,
    fontWeight: "bold",
    color: "#526475",
    lineHeight: 1.35,
  },

  procedureContent: {
    width: "69%",
    padding: 7,
    fontSize: 8.2,
    lineHeight: 1.4,
    color: "#34495A",
    backgroundColor: "#FFFFFF",
  },

  footer: {
    position: "absolute",
    left: 30,
    right: 30,
    bottom: 17,
    borderTopWidth: 1,
    borderTopColor: "#D9E1E8",
    paddingTop: 5,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  footerText: {
    fontSize: 7,
    color: "#8492A0",
  },
});

/* =========================================================
   PDF INFORMATION ROW
========================================================= */

const PDFInformationRow = ({
  label,
  value,
}) => {
  return (
    <View style={pdfStyles.informationRow}>
      <Text style={pdfStyles.informationLabel}>
        {label}:
      </Text>

      <Text style={pdfStyles.informationValue}>
        {safeText(value)}
      </Text>
    </View>
  );
};

/* =========================================================
   PDF SECTION
========================================================= */

const PDFSection = ({
  title,
  children,
}) => {
  return (
    <View
      style={pdfStyles.section}
      wrap
      minPresenceAhead={55}
    >
      <Text
        style={pdfStyles.sectionTitle}
        minPresenceAhead={25}
      >
        {title}
      </Text>

      <View style={pdfStyles.sectionBody}>
        {children}
      </View>
    </View>
  );
};

/* =========================================================
   PROCEDURE ROW
========================================================= */

const PDFProcedureRow = ({
  label,
  content,
  last = false,
}) => {
  return (
    <View
      style={[
        pdfStyles.procedureRow,
        last
          ? {
              borderBottomWidth: 0,
            }
          : {},
      ]}
      wrap
      minPresenceAhead={45}
    >
      <Text style={pdfStyles.procedureLabel}>
        {label}
      </Text>

      <Text style={pdfStyles.procedureContent}>
        {safeText(content)}
      </Text>
    </View>
  );
};

/* =========================================================
   SINGLE LESSON PLAN PDF
========================================================= */

const LessonPlanPDFPage = ({
  plan,
  pageIndex,
  totalPages,
}) => {
  const materials = normalizeMaterials(plan);

  return (
    <Page
      size="A4"
      orientation="portrait"
      style={pdfStyles.page}
      wrap
    >
      {/* SCHOOL HEADER */}

      <View
        style={pdfStyles.schoolHeader}
        wrap={false}
      >
        <Image
          src={logo}
          style={pdfStyles.logo}
        />

        <View style={pdfStyles.schoolHeaderContent}>
          <Text style={pdfStyles.schoolName}>
            SALVATION IN CHRIST SCHOOL SYSTEM
          </Text>

          <Text style={pdfStyles.schoolAddress}>
            Zazay Community, Paynesville City,
            Republic of Liberia
          </Text>

          <Text style={pdfStyles.schoolContact}>
            Contacts: +231-776494010 /
            +231-778582888 / +231-770540258
          </Text>

          <Text style={pdfStyles.schoolContact}>
            Email:
            salvationinchristschoolsystem@gmail.com
          </Text>
        </View>
      </View>

      {/* DOCUMENT TITLE */}

      <View
        style={pdfStyles.documentTitle}
        wrap={false}
      >
        <Text style={pdfStyles.documentTitleText}>
          WEEKLY LESSON PLAN
        </Text>
      </View>

      {/* BASIC INFORMATION */}

      <View
        style={pdfStyles.topInformation}
        wrap={false}
      >
        <View style={pdfStyles.topInformationColumn}>
          <PDFInformationRow
            label="Teacher"
            value={plan.teacher_name}
          />

          <PDFInformationRow
            label="Grade"
            value={plan.grade_class_name}
          />

          <PDFInformationRow
            label="Subject"
            value={plan.subject_name}
          />

          <PDFInformationRow
            label="Topic"
            value={plan.topic}
          />
        </View>

        <View
          style={
            pdfStyles.topInformationColumnRight
          }
        >
          <PDFInformationRow
            label="Date"
            value={formatDate(
              plan.lesson_date
            )}
          />

          <PDFInformationRow
            label="Duration"
            value={
              plan.duration
                ? `${plan.duration} minutes`
                : "-"
            }
          />

          <PDFInformationRow
            label="Period"
            value={plan.period_name}
          />

          <PDFInformationRow
            label="Week"
            value={`Week ${safeText(
              plan.week
            )}`}
          />
        </View>
      </View>

      {/* TOPIC */}

      <View
        style={pdfStyles.section}
        wrap
        minPresenceAhead={50}
      >
        <Text style={pdfStyles.sectionTitle}>
          Topic
        </Text>

        <View style={pdfStyles.topicBody}>
          <Text style={pdfStyles.topicText}>
            {safeText(plan.topic)}
          </Text>
        </View>
      </View>

      {/* OBJECTIVES */}

      <PDFSection title="Instructional Objectives">
        <Text style={pdfStyles.objectiveText}>
          {safeText(plan.objectives)}
        </Text>
      </PDFSection>

      {/* TEACHING MATERIALS */}

      <PDFSection title="Teaching Materials">
        {materials.length > 0 ? (
          <View
            style={pdfStyles.materialsContainer}
          >
            {materials.map(
              (material, index) => (
                <View
                  key={`${material}-${index}`}
                  style={pdfStyles.material}
                  wrap={false}
                >
                  <Text
                    style={
                      pdfStyles.materialText
                    }
                  >
                    ✓ {materialLabel(material)}
                  </Text>
                </View>
              )
            )}
          </View>
        ) : (
          <Text
            style={pdfStyles.objectiveText}
          >
            No teaching materials recorded.
          </Text>
        )}
      </PDFSection>

      {/* TEACHING AID */}

      <View
        style={pdfStyles.section}
        wrap
        minPresenceAhead={55}
      >
        <Text style={pdfStyles.sectionTitle}>
          Teaching Aid
        </Text>

        <View style={pdfStyles.teachingAidBox}>
          <Text
            style={pdfStyles.teachingAidText}
          >
            {safeText(plan.teaching_aid)}
          </Text>
        </View>
      </View>

      {/* TEACHING METHOD */}

      <PDFSection title="Teaching Method">
        <Text style={pdfStyles.methodText}>
          {safeText(plan.teaching_method)}
        </Text>
      </PDFSection>

      {/* PRESENTATION & PROCEDURES */}

      <View
        style={pdfStyles.procedureSection}
        wrap
        minPresenceAhead={65}
      >
        <View style={pdfStyles.procedureHeader}>
          <Text
            style={
              pdfStyles.procedureHeaderText
            }
          >
            Presentation & Procedures
            (Classroom Activities)
          </Text>
        </View>

        <PDFProcedureRow
          label={
            "Initiating Activities / Introduction"
          }
          content={
            plan.initiating_activities
          }
        />

        <PDFProcedureRow
          label={
            "Developmental Association / Main Activities"
          }
          content={
            plan.developmental_activities
          }
        />

        <PDFProcedureRow
          label="Summary / Conclusion"
          content={
            plan.summary_conclusion
          }
          last
        />
      </View>

      {/* EVALUATION */}

      <PDFSection title="Evaluation">
        <Text style={pdfStyles.methodText}>
          {safeText(plan.evaluation)}
        </Text>
      </PDFSection>

      {/* ASSIGNMENT */}

      <PDFSection title="Assignment">
        <Text style={pdfStyles.methodText}>
          {safeText(plan.assignment)}
        </Text>
      </PDFSection>

      {/* FOOTER */}

      <View
        style={pdfStyles.footer}
        fixed
      >
        <Text style={pdfStyles.footerText}>
          Official Weekly Lesson Plan Record
        </Text>

        <Text style={pdfStyles.footerText}>
          Salvation In Christ School System
        </Text>

        <Text
          style={pdfStyles.footerText}
          render={({ pageNumber }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        >
          Page {pageIndex} of {totalPages}
        </Text>
      </View>
    </Page>
  );
};

/* =========================================================
   COMPLETE PDF DOCUMENT
========================================================= */

const LessonPlanPDF = ({
  lessonPlans = [],
}) => {
  return (
    <Document>
      {lessonPlans.map(
        (plan, index) => (
          <LessonPlanPDFPage
            key={
              plan.id ||
              `lesson-plan-${index}`
            }
            plan={plan}
            pageIndex={index + 1}
            totalPages={
              lessonPlans.length
            }
          />
        )
      )}
    </Document>
  );
};

/* =========================================================
   TEACHING MATERIAL EDITOR
========================================================= */

const TeachingMaterialsEditor = ({
  value = [],
  onChange,
}) => {
  const handleMaterialChange = (
    material
  ) => {
    const current = Array.isArray(value)
      ? value
      : [];

    const exists =
      current.includes(material);

    if (exists) {
      onChange(
        current.filter(
          (item) => item !== material
        )
      );

      return;
    }

    onChange([
      ...current,
      material,
    ]);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        p: 1.5,
        backgroundColor: "#FAFAFA",
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          color: COLORS.black,
          mb: 1.2,
          fontSize: "0.9rem",
        }}
      >
        Teaching Materials
      </Typography>

      <Grid
        container
        spacing={1}
      >
        {TEACHING_MATERIALS.map(
          (material) => {
            const checked =
              Array.isArray(value) &&
              value.includes(
                material.value
              );

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={material.value}
              >
                <Box
                  onClick={() =>
                    handleMaterialChange(
                      material.value
                    )
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 44,
                    px: 1,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    border: checked
                      ? `1.5px solid ${COLORS.orange}`
                      : `1px solid ${COLORS.border}`,
                    backgroundColor: checked
                      ? "#FFF4E8"
                      : COLORS.white,
                    transition:
                      "all 0.2s ease",
                    "&:hover": {
                      borderColor:
                        COLORS.orange,
                      backgroundColor:
                        "#FFF8F1",
                    },
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() =>
                      handleMaterialChange(
                        material.value
                      )
                    }
                    size="small"
                    sx={{
                      p: 0.5,
                      mr: 0.5,
                      color:
                        COLORS.camel,
                      "&.Mui-checked": {
                        color:
                          COLORS.orange,
                      },
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize:
                        "0.82rem",
                      fontWeight:
                        checked
                          ? 700
                          : 500,
                      color:
                        COLORS.darkGray,
                    }}
                  >
                    {material.label}
                  </Typography>
                </Box>
              </Grid>
            );
          }
        )}
      </Grid>
    </Box>
  );
};

/* =========================================================
   INFO BLOCK
========================================================= */

const InfoBlock = ({
  icon,
  label,
  value,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        backgroundColor:
          COLORS.lightCamel,
        border:
          `1px solid ${COLORS.border}`,
        p: 1.2,
        borderRadius: 1.5,
        minHeight: 62,
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor:
            COLORS.white,
          color:
            COLORS.darkOrange,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box
        sx={{
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize:
              "0.67rem",
            color:
              COLORS.gray,
            fontWeight:
              800,
            textTransform:
              "uppercase",
            letterSpacing:
              "0.4px",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontWeight: 800,
            color:
              COLORS.black,
            fontSize:
              "0.88rem",
            overflowWrap:
              "anywhere",
          }}
        >
          {value || "-"}
        </Typography>
      </Box>
    </Box>
  );
};

/* =========================================================
   CONTENT BLOCK
========================================================= */

const ContentBlock = ({
  title,
  content,
  accent = COLORS.orange,
}) => {
  return (
    <Box
      sx={{
        borderLeft:
          `4px solid ${accent}`,
        backgroundColor:
          COLORS.cream,
        border:
          `1px solid ${COLORS.border}`,
        borderLeftWidth: 4,
        p: 1.6,
        borderRadius:
          "0 10px 10px 0",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          color:
            COLORS.black,
          mb: 0.8,
          fontSize:
            "0.9rem",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color:
            COLORS.darkGray,
          fontSize:
            "0.88rem",
          lineHeight:
            1.65,
          whiteSpace:
            "pre-wrap",
          overflowWrap:
            "anywhere",
        }}
      >
        {content ||
          "No information provided."}
      </Typography>
    </Box>
  );
};

/* =========================================================
   MATERIAL DISPLAY
========================================================= */

const MaterialsDisplay = ({
  materials = [],
}) => {
  return (
    <Box
      sx={{
        border:
          `1px solid ${COLORS.border}`,
        borderRadius: 2,
        backgroundColor:
          "#FAFAFA",
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          fontSize:
            "0.92rem",
          color:
            COLORS.black,
          mb: 1,
        }}
      >
        Teaching Materials
      </Typography>

      {materials.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.8,
          }}
        >
          {materials.map(
            (material, index) => (
              <Chip
                key={`${material}-${index}`}
                icon={
                  <CheckCircle
                    sx={{
                      fontSize:
                        "16px !important",
                    }}
                  />
                }
                label={materialLabel(
                  material
                )}
                size="small"
                sx={{
                  backgroundColor:
                    "#FFF2E5",
                  color:
                    COLORS.darkOrange,
                  border:
                    `1px solid ${COLORS.camel}`,
                  fontWeight: 700,
                  "& .MuiChip-icon": {
                    color:
                      COLORS.orange,
                  },
                }}
              />
            )
          )}
        </Box>
      ) : (
        <Typography
          sx={{
            fontSize:
              "0.82rem",
            color:
              COLORS.gray,
          }}
        >
          No teaching materials recorded.
        </Typography>
      )}
    </Box>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const ViewLessonPlan = () => {
  const [
    lessonPlans,
    setLessonPlans,
  ] = useState([]);

  const [
    editingId,
    setEditingId,
  ] = useState(null);

  const [
    editedData,
    setEditedData,
  ] = useState({});

  const [
    periods,
    setPeriods,
  ] = useState([]);

  const [
    selectedPeriod,
    setSelectedPeriod,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const accessToken =
    localStorage.getItem("access");

  /* =======================================================
     FETCH
  ======================================================= */

  useEffect(() => {
    fetchLessonPlans();
    fetchPeriods();
  }, []);

  const fetchLessonPlans =
    async () => {
      try {
        setLoading(true);

        const res =
          await api.get(
            "/lesson-plans/",
            {
              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
              },
            }
          );

        setLessonPlans(
          Array.isArray(res.data)
            ? res.data
            : []
        );
      } catch (error) {
        console.error(
          "Lesson plan loading error:",
          error
        );

        Swal.fire(
          "Error",
          "Unable to load lesson plans.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

  const fetchPeriods =
    async () => {
      try {
        const res =
          await api.get(
            "/api/users/periods/",
            {
              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
              },
            }
          );

        setPeriods(
          Array.isArray(res.data)
            ? res.data
            : []
        );
      } catch (error) {
        console.error(
          "Period loading error:",
          error
        );
      }
    };

  /* =======================================================
     FILTER
  ======================================================= */

  const filteredPlans =
    useMemo(() => {
      const searchText =
        search
          .trim()
          .toLowerCase();

      return lessonPlans.filter(
        (plan) => {
          const matchesPeriod =
            !selectedPeriod ||
            plan.period_name ===
              selectedPeriod;

          const matchesSearch =
            !searchText ||
            plan.topic
              ?.toLowerCase()
              .includes(searchText) ||
            plan.subject_name
              ?.toLowerCase()
              .includes(searchText) ||
            plan.grade_class_name
              ?.toLowerCase()
              .includes(searchText) ||
            plan.teacher_name
              ?.toLowerCase()
              .includes(searchText) ||
            String(plan.week || "")
              .toLowerCase()
              .includes(searchText);

          return (
            matchesPeriod &&
            matchesSearch
          );
        }
      );
    }, [
      lessonPlans,
      selectedPeriod,
      search,
    ]);

  /* =======================================================
     EDIT
  ======================================================= */

  const handleEdit = (
    plan
  ) => {
    setEditingId(plan.id);

    setEditedData({
      ...plan,

      teaching_materials:
        normalizeMaterials(plan),

      teaching_aid:
        plan.teaching_aid ?? "",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =======================================================
     EDIT FIELD
  ======================================================= */

  const updateEditedField = (
    field,
    value
  ) => {
    setEditedData(
      (previous) => ({
        ...previous,
        [field]: value,
      })
    );
  };

  /* =======================================================
     UPDATE
  ======================================================= */

  const handleUpdate = async (
    id
  ) => {
    try {
      const payload = {
        subject:
          editedData.subject,

        grade_class:
          editedData.grade_class,

        period:
          editedData.period,

        topic:
          editedData.topic,

        objectives:
          editedData.objectives,

        lesson_date:
          editedData.lesson_date,

        duration:
          editedData.duration,

        teaching_materials:
          Array.isArray(
            editedData.teaching_materials
          )
            ? editedData.teaching_materials
            : [],

        teaching_aid:
          editedData.teaching_aid ?? "",

        teaching_method:
          editedData.teaching_method,

        initiating_activities:
          editedData.initiating_activities,

        developmental_activities:
          editedData.developmental_activities,

        summary_conclusion:
          editedData.summary_conclusion,

        evaluation:
          editedData.evaluation,

        assignment:
          editedData.assignment || "",

        week:
          editedData.week,
      };

      await api.put(
        `/lesson-plans/${id}/`,
        payload,
        {
          headers: {
            Authorization:
              `Bearer ${accessToken}`,
          },
        }
      );

      Swal.fire(
        "Success",
        "Lesson plan updated successfully.",
        "success"
      );

      setEditingId(null);
      setEditedData({});

      await fetchLessonPlans();
    } catch (error) {
      console.error(
        "Lesson plan update error:",
        error
      );

      const errorData =
        error.response?.data || {};

      let message =
        "Unable to update lesson plan.";

      if (
        typeof errorData ===
        "object"
      ) {
        const messages =
          Object.entries(
            errorData
          )
            .map(
              ([key, value]) =>
                `${key}: ${
                  Array.isArray(value)
                    ? value.join(", ")
                    : value
                }`
            )
            .join("\n");

        if (messages) {
          message = messages;
        }
      }

      Swal.fire(
        "Error",
        message,
        "error"
      );
    }
  };

  /* =======================================================
     DELETE
  ======================================================= */

  const handleDelete = (
    id
  ) => {
    Swal.fire({
      title:
        "Delete Lesson Plan?",

      text:
        "This action cannot be undone.",

      icon:
        "warning",

      showCancelButton:
        true,

      confirmButtonText:
        "Yes, Delete",

      confirmButtonColor:
        COLORS.red,

      cancelButtonColor:
        COLORS.black,
    }).then(
      async (result) => {
        if (
          !result.isConfirmed
        ) {
          return;
        }

        try {
          await api.delete(
            `/lesson-plans/${id}/`,
            {
              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
              },
            }
          );

          Swal.fire(
            "Deleted",
            "Lesson plan deleted successfully.",
            "success"
          );

          await fetchLessonPlans();
        } catch (error) {
          Swal.fire(
            "Error",
            "Unable to delete lesson plan.",
            "error"
          );
        }
      }
    );
  };

  /* =======================================================
     DOWNLOAD DATA
  ======================================================= */

  const downloadPlans =
    selectedPeriod
      ? filteredPlans
      : lessonPlans;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Box
      sx={{
        minHeight:
          "100vh",

        background:
          "linear-gradient(180deg, #FBF8F3 0%, #F5F5F5 100%)",

        px: {
          xs: 1.2,
          sm: 2.5,
          md: 4,
        },

        py: {
          xs: 1.5,
          sm: 2.5,
          md: 4,
        },
      }}
    >
      <Box
        sx={{
          maxWidth:
            1400,

          mx:
            "auto",
        }}
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            overflow:
              "hidden",

            borderRadius:
              {
                xs: 2,
                md: 3,
              },

            border:
              `1px solid ${COLORS.border}`,

            background:
              "linear-gradient(135deg, #111111 0%, #242424 100%)",

            mb:
              2.5,
          }}
        >
          <Box
            sx={{
              p: {
                xs: 2,
                sm: 3,
                md: 3.5,
              },

              borderBottom:
                `5px solid ${COLORS.orange}`,
            }}
          >
            <Stack
              direction={{
                xs:
                  "column",
                md:
                  "row",
              }}
              justifyContent={
                "space-between"
              }
              alignItems={{
                xs:
                  "flex-start",
                md:
                  "center",
              }}
              spacing={2}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                  sx={{
                    mb: 0.5,
                  }}
                >
                  <MenuBook
                    sx={{
                      color:
                        COLORS.orange,
                    }}
                  />

                  <Typography
                    sx={{
                      color:
                        COLORS.white,

                      fontWeight:
                        900,

                      fontSize:
                        {
                          xs:
                            "1.35rem",
                          sm:
                            "1.7rem",
                          md:
                            "2rem",
                        },
                    }}
                  >
                    Weekly Lesson Plans
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color:
                      "#D1D5DB",

                    fontSize:
                      {
                        xs:
                          "0.8rem",
                        md:
                          "0.92rem",
                      },

                    maxWidth:
                      750,

                    lineHeight:
                      1.6,
                  }}
                >
                  Create, review, edit and
                  download professional
                  weekly lesson-plan records
                  following the official school
                  lesson-plan structure.
                </Typography>
              </Box>

              <Box
                sx={{
                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    1,

                  px:
                    2,

                  py:
                    1.1,

                  borderRadius:
                    2,

                  backgroundColor:
                    COLORS.orange,

                  color:
                    COLORS.black,
                }}
              >
                <MenuBook />

                <Typography
                  fontWeight={
                    900
                  }
                >
                  {lessonPlans.length} Plans
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>

        {/* =================================================
            FILTER PANEL
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p:
              {
                xs: 1.5,
                sm: 2,
              },

            mb:
              2.5,

            borderRadius:
              2,

            border:
              `1px solid ${COLORS.border}`,

            backgroundColor:
              COLORS.white,
          }}
        >
          <Grid
            container
            spacing={1.5}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={5}
            >
              <TextField
                fullWidth
                size="small"
                label="Search lesson plans"
                placeholder="Topic, subject, class, teacher or week..."
                value={search}
                onChange={(e) =>
                  setSearch(
                    e.target.value
                  )
                }
                sx={
                  inputStyles
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                    >
                      <Search
                        fontSize="small"
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={2.5}
            >
              <TextField
                select
                fullWidth
                size="small"
                label="Filter by Period"
                value={
                  selectedPeriod
                }
                onChange={(e) =>
                  setSelectedPeriod(
                    e.target.value
                  )
                }
                sx={
                  inputStyles
                }
              >
                <MenuItem value="">
                  All Periods
                </MenuItem>

                {periods.map(
                  (period) => (
                    <MenuItem
                      key={period.id}
                      value={
                        period.name
                      }
                    >
                      {period.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={1.8}
            >
              <Button
                fullWidth
                variant="outlined"
                startIcon={
                  <Close />
                }
                onClick={() => {
                  setSearch("");
                  setSelectedPeriod("");
                }}
                sx={{
                  height: 40,

                  borderColor:
                    COLORS.black,

                  color:
                    COLORS.black,

                  fontWeight:
                    700,

                  textTransform:
                    "none",

                  "&:hover": {
                    borderColor:
                      COLORS.orange,

                    backgroundColor:
                      "#FFF8F1",
                  },
                }}
              >
                Clear
              </Button>
            </Grid>

            <Grid
              item
              xs={12}
              md={2.7}
            >
              {downloadPlans.length >
              0 ? (
                <PDFDownloadLink
                  document={
                    <LessonPlanPDF
                      lessonPlans={
                        downloadPlans
                      }
                    />
                  }
                  fileName={
                    selectedPeriod
                      ? `weekly_lesson_plans_${selectedPeriod}.pdf`
                      : "weekly_lesson_plans.pdf"
                  }
                  style={{
                    textDecoration:
                      "none",

                    width:
                      "100%",
                  }}
                >
                  {({
                    loading:
                      pdfLoading,
                  }) => (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={
                        <Download />
                      }
                      disabled={
                        pdfLoading
                      }
                      sx={{
                        height: 40,

                        backgroundColor:
                          COLORS.orange,

                        color:
                          COLORS.black,

                        fontWeight:
                          900,

                        textTransform:
                          "none",

                        "&:hover": {
                          backgroundColor:
                            COLORS.darkOrange,

                          color:
                            COLORS.white,
                        },
                      }}
                    >
                      {pdfLoading
                        ? "Preparing PDF..."
                        : `Download ${
                            downloadPlans.length
                          } PDF ${
                            downloadPlans.length ===
                            1
                              ? "Page"
                              : "Pages"
                          }`}
                    </Button>
                  )}
                </PDFDownloadLink>
              ) : (
                <Button
                  fullWidth
                  disabled
                  variant="contained"
                  startIcon={
                    <Download />
                  }
                  sx={{
                    height:
                      40,

                    textTransform:
                      "none",
                  }}
                >
                  No Data
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* =================================================
            SUMMARY
        ================================================= */}

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{
            mb:
              2,
          }}
        >
          <Chip
            icon={
              <MenuBook />
            }
            label={`${filteredPlans.length} Showing`}
            sx={{
              backgroundColor:
                COLORS.black,

              color:
                COLORS.white,

              fontWeight:
                700,
            }}
          />

          {selectedPeriod && (
            <Chip
              icon={
                <CalendarMonth />
              }
              label={
                selectedPeriod
              }
              sx={{
                backgroundColor:
                  COLORS.lightCamel,

                color:
                  COLORS.black,

                fontWeight:
                  700,
              }}
            />
          )}

          {search && (
            <Chip
              icon={
                <Search />
              }
              label={`Search: ${search}`}
              onDelete={() =>
                setSearch("")
              }
              sx={{
                backgroundColor:
                  "#FFF2E5",

                color:
                  COLORS.darkOrange,

                fontWeight:
                  700,
              }}
            />
          )}
        </Stack>

        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              minHeight:
                300,

              display:
                "flex",

              alignItems:
                "center",

              justifyContent:
                "center",

              border:
                `1px solid ${COLORS.border}`,
            }}
          >
            <Stack
              spacing={1.5}
              alignItems="center"
            >
              <CircularProgress
                sx={{
                  color:
                    COLORS.orange,
                }}
              />

              <Typography
                color="text.secondary"
              >
                Loading lesson plans...
              </Typography>
            </Stack>
          </Paper>
        ) : filteredPlans.length ===
          0 ? (
          <Paper
            elevation={0}
            sx={{
              p:
                {
                  xs: 4,
                  md: 7,
                },

              textAlign:
                "center",

              borderRadius:
                3,

              border:
                `1px dashed ${COLORS.camel}`,

              backgroundColor:
                COLORS.white,
            }}
          >
            <MenuBook
              sx={{
                fontSize:
                  55,

                color:
                  COLORS.camel,

                mb:
                  1,
              }}
            />

            <Typography
              variant="h6"
              fontWeight={
                900
              }
            >
              No lesson plans found
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                mt:
                  0.5,
              }}
            >
              Try changing your
              search or period filter.
            </Typography>
          </Paper>
        ) : (
          <Stack
            spacing={2}
          >
            {filteredPlans.map(
              (plan) => (
                <Paper
                  key={plan.id}
                  elevation={0}
                  sx={{
                    overflow:
                      "hidden",

                    borderRadius:
                      2.5,

                    border:
                      `1px solid ${COLORS.border}`,

                    backgroundColor:
                      COLORS.white,

                    transition:
                      "0.2s ease",

                    "&:hover": {
                      boxShadow:
                        "0 12px 35px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* =======================================
                      CARD HEADER
                  ======================================= */}

                  <Box
                    sx={{
                      background:
                        "linear-gradient(135deg, #111111 0%, #252525 100%)",

                      color:
                        COLORS.white,

                      px:
                        {
                          xs: 1.5,
                          sm: 2,
                          md: 2.5,
                        },

                      py:
                        1.6,

                      display:
                        "flex",

                      flexWrap:
                        "wrap",

                      alignItems:
                        "center",

                      justifyContent:
                        "space-between",

                      gap:
                        1.5,

                      borderBottom:
                        `4px solid ${COLORS.orange}`,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth:
                          0,

                        flex:
                          1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight:
                            900,

                          fontSize:
                            {
                              xs:
                                "1rem",
                              md:
                                "1.2rem",
                            },

                          overflowWrap:
                            "anywhere",
                        }}
                      >
                        {safeText(
                          plan.topic
                        )}
                      </Typography>

                      <Typography
                        sx={{
                          color:
                            "#D1D5DB",

                          fontSize:
                            "0.8rem",

                          mt:
                            0.3,
                        }}
                      >
                        {safeText(
                          plan.subject_name
                        )}
                        {" • "}
                        {safeText(
                          plan.grade_class_name
                        )}
                        {" • "}
                        {safeText(
                          plan.period_name
                        )}
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={0.7}
                      alignItems="center"
                    >
                      <Chip
                        label={`Week ${safeText(
                          plan.week
                        )}`}
                        size="small"
                        sx={{
                          backgroundColor:
                            COLORS.orange,

                          color:
                            COLORS.black,

                          fontWeight:
                            900,
                        }}
                      />

                      <IconButton
                        size="small"
                        onClick={() =>
                          handleEdit(
                            plan
                          )
                        }
                        sx={{
                          color:
                            COLORS.white,

                          backgroundColor:
                            "rgba(255,255,255,0.1)",

                          "&:hover": {
                            backgroundColor:
                              COLORS.orange,

                            color:
                              COLORS.black,
                          },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDelete(
                            plan.id
                          )
                        }
                        sx={{
                          color:
                            "#FF8A80",

                          backgroundColor:
                            "rgba(255,255,255,0.1)",

                          "&:hover": {
                            backgroundColor:
                              COLORS.red,

                            color:
                              COLORS.white,
                          },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>

                  {/* =======================================
                      EDIT MODE
                  ======================================= */}

                  {editingId ===
                  plan.id ? (
                    <Box
                      sx={{
                        p:
                          {
                            xs: 1.5,
                            sm: 2.5,
                            md: 3,
                          },
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight:
                            900,

                          fontSize:
                            "1.05rem",

                          mb:
                            2,

                          color:
                            COLORS.black,
                        }}
                      >
                        Edit Weekly Lesson Plan
                      </Typography>

                      <Grid
                        container
                        spacing={2}
                      >
                        <Grid
                          item
                          xs={12}
                          md={6}
                        >
                          <TextField
                            fullWidth
                            label="Topic"
                            value={
                              editedData.topic ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "topic",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                        >
                          <TextField
                            fullWidth
                            label="Week"
                            value={
                              editedData.week ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "week",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                        >
                          <TextField
                            fullWidth
                            type="number"
                            label="Duration (Minutes)"
                            value={
                              editedData.duration ??
                              45
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "duration",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                        >
                          <TextField
                            fullWidth
                            type="date"
                            label="Lesson Date"
                            value={
                              editedData.lesson_date ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "lesson_date",
                                e.target.value
                              )
                            }
                            InputLabelProps={{
                              shrink:
                                true,
                            }}
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                        >
                          <TextField
                            fullWidth
                            label="Subject"
                            value={
                              editedData.subject_name ||
                              "-"
                            }
                            disabled
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                        >
                          <TextField
                            fullWidth
                            label="Grade Class"
                            value={
                              editedData.grade_class_name ||
                              "-"
                            }
                            disabled
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Instructional Objectives"
                            value={
                              editedData.objectives ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "objectives",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <TeachingMaterialsEditor
                            value={
                              editedData.teaching_materials ||
                              []
                            }
                            onChange={(
                              value
                            ) =>
                              updateEditedField(
                                "teaching_materials",
                                value
                              )
                            }
                          />
                        </Grid>

                        {/* =================================
                            TEACHING AID
                        ================================= */}

                        <Grid
                          item
                          xs={12}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Teaching Aid"
                            placeholder="Enter the teaching aid used for this lesson..."
                            value={
                              editedData.teaching_aid ??
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "teaching_aid",
                                e.target.value
                              )
                            }
                            helperText="This field is saved directly to the backend teaching_aid field."
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Teaching Method"
                            value={
                              editedData.teaching_method ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "teaching_method",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <Divider
                            sx={{
                              my:
                                0.5,
                            }}
                          />

                          <Typography
                            sx={{
                              fontWeight:
                                900,

                              fontSize:
                                "1rem",

                              mt:
                                1,

                              mb:
                                1.5,
                            }}
                          >
                            Presentation & Procedures
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Initiating Activities / Introduction"
                            value={
                              editedData.initiating_activities ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "initiating_activities",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={5}
                            label="Developmental Association / Main Activities"
                            value={
                              editedData.developmental_activities ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "developmental_activities",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Summary / Conclusion"
                            value={
                              editedData.summary_conclusion ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "summary_conclusion",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={6}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Evaluation"
                            value={
                              editedData.evaluation ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "evaluation",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={6}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Assignment"
                            value={
                              editedData.assignment ||
                              ""
                            }
                            onChange={(e) =>
                              updateEditedField(
                                "assignment",
                                e.target.value
                              )
                            }
                            sx={
                              inputStyles
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <Stack
                            direction={{
                              xs:
                                "column",
                              sm:
                                "row",
                            }}
                            spacing={1}
                          >
                            <Button
                              variant="contained"
                              startIcon={
                                <Save />
                              }
                              onClick={() =>
                                handleUpdate(
                                  plan.id
                                )
                              }
                              sx={{
                                backgroundColor:
                                  COLORS.orange,

                                color:
                                  COLORS.black,

                                fontWeight:
                                  900,

                                textTransform:
                                  "none",

                                "&:hover": {
                                  backgroundColor:
                                    COLORS.darkOrange,

                                  color:
                                    COLORS.white,
                                },
                              }}
                            >
                              Save Changes
                            </Button>

                            <Button
                              variant="outlined"
                              startIcon={
                                <Close />
                              }
                              onClick={() => {
                                setEditingId(
                                  null
                                );

                                setEditedData(
                                  {}
                                );
                              }}
                              sx={{
                                borderColor:
                                  COLORS.black,

                                color:
                                  COLORS.black,

                                fontWeight:
                                  700,

                                textTransform:
                                  "none",
                              }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    /* =====================================
                       VIEW MODE
                    ===================================== */

                    <Box
                      sx={{
                        p:
                          {
                            xs: 1.5,
                            sm: 2,
                            md: 2.5,
                          },
                      }}
                    >
                      {/* BASIC DETAILS */}

                      <Grid
                        container
                        spacing={1.2}
                        sx={{
                          mb:
                            2,
                        }}
                      >
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                        >
                          <InfoBlock
                            icon={
                              <School fontSize="small" />
                            }
                            label="Grade"
                            value={
                              plan.grade_class_name
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                        >
                          <InfoBlock
                            icon={
                              <MenuBook fontSize="small" />
                            }
                            label="Subject"
                            value={
                              plan.subject_name
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                        >
                          <InfoBlock
                            icon={
                              <CalendarMonth fontSize="small" />
                            }
                            label="Lesson Date"
                            value={
                              formatDate(
                                plan.lesson_date
                              )
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                        >
                          <InfoBlock
                            icon={
                              <Timer fontSize="small" />
                            }
                            label="Duration"
                            value={
                              plan.duration
                                ? `${plan.duration} minutes`
                                : "-"
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                        >
                          <InfoBlock
                            icon={
                              <Assessment fontSize="small" />
                            }
                            label="Period"
                            value={
                              plan.period_name
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                        >
                          <InfoBlock
                            icon={
                              <MenuBook fontSize="small" />
                            }
                            label="Week"
                            value={`Week ${safeText(
                              plan.week
                            )}`}
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={6}
                        >
                          <InfoBlock
                            icon={
                              <School fontSize="small" />
                            }
                            label="Teacher"
                            value={
                              plan.teacher_name
                            }
                          />
                        </Grid>
                      </Grid>

                      {/* TOPIC */}

                      <Box
                        sx={{
                          mb:
                            1.5,

                          border:
                            `1px solid ${COLORS.border}`,

                          borderRadius:
                            2,

                          overflow:
                            "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor:
                              COLORS.orange,

                            px:
                              1.5,

                            py:
                              0.8,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight:
                                900,

                              fontSize:
                                "0.8rem",

                              textTransform:
                                "uppercase",
                            }}
                          >
                            Topic
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            p:
                              1.5,

                            backgroundColor:
                              COLORS.cream,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight:
                                900,

                              fontSize:
                                {
                                  xs:
                                    "1rem",
                                  md:
                                    "1.1rem",
                                },

                              color:
                                COLORS.black,

                              overflowWrap:
                                "anywhere",
                            }}
                          >
                            {safeText(
                              plan.topic
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      {/* OBJECTIVES / MATERIALS / AID / METHOD */}

                      <Grid
                        container
                        spacing={1.5}
                      >
                        <Grid
                          item
                          xs={12}
                        >
                          <ContentBlock
                            title="Instructional Objectives"
                            content={
                              plan.objectives
                            }
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <MaterialsDisplay
                            materials={normalizeMaterials(
                              plan
                            )}
                          />
                        </Grid>

                        {/* TEACHING AID */}

                        <Grid
                          item
                          xs={12}
                          md={6}
                        >
                          <ContentBlock
                            title="Teaching Aid"
                            content={
                              plan.teaching_aid
                            }
                            accent={
                              COLORS.orange
                            }
                          />
                        </Grid>

                        {/* TEACHING METHOD */}

                        <Grid
                          item
                          xs={12}
                          md={6}
                        >
                          <ContentBlock
                            title="Teaching Method"
                            content={
                              plan.teaching_method
                            }
                          />
                        </Grid>

                        {/* EVALUATION */}

                        <Grid
                          item
                          xs={12}
                        >
                          <ContentBlock
                            title="Evaluation"
                            content={
                              plan.evaluation
                            }
                            accent={
                              COLORS.green
                            }
                          />
                        </Grid>
                      </Grid>

                      {/* PRESENTATION */}

                      <Box
                        sx={{
                          mt:
                            1.5,

                          border:
                            `1px solid ${COLORS.border}`,

                          borderRadius:
                            2,

                          overflow:
                            "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor:
                              COLORS.black,

                            color:
                              COLORS.white,

                            px:
                              1.5,

                            py:
                              1,

                            fontWeight:
                              900,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight:
                                900,

                              fontSize:
                                "0.9rem",
                            }}
                          >
                            Presentation & Procedures
                            (Classroom Activities)
                          </Typography>
                        </Box>

                        <Grid
                          container
                        >
                          <Grid
                            item
                            xs={12}
                          >
                            <ContentBlock
                              title="Initiating Activities / Introduction"
                              content={
                                plan.initiating_activities
                              }
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                          >
                            <ContentBlock
                              title="Developmental Association / Main Activities"
                              content={
                                plan.developmental_activities
                              }
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                          >
                            <ContentBlock
                              title="Summary / Conclusion"
                              content={
                                plan.summary_conclusion
                              }
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      {/* ASSIGNMENT */}

                      <Box
                        sx={{
                          mt:
                            1.5,
                        }}
                      >
                        <ContentBlock
                          title="Assignment"
                          content={
                            plan.assignment
                          }
                          accent={
                            COLORS.blue
                          }
                        />
                      </Box>
                    </Box>
                  )}
                </Paper>
              )
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default ViewLessonPlan;